import { ProyectoMock } from '../../../mocks/proyectos';

interface Props {
  proyectos: ProyectoMock[];
}

const REQUISITOS = [
  {
    icon: 'ri-user-line',
    titulo: 'Datos del Investigador Principal',
    color: 'teal',
    items: [
      'Nombre completo del investigador responsable',
      'Correo electrónico institucional',
      'Sede (Oruro / Cochabamba)',
      'Carrera a la que pertenece',
      'Teléfono de contacto (opcional)',
    ],
  },
  {
    icon: 'ri-bookmark-line',
    titulo: 'Identificación del Proyecto',
    color: 'teal',
    items: [
      'Título completo del proyecto de investigación',
      'Resumen ejecutivo (máx. 500 caracteres)',
      'Objetivo general de la investigación',
      'Objetivos específicos (al menos uno)',
    ],
  },
  {
    icon: 'ri-draft-line',
    titulo: 'Diseño y Metodología',
    color: 'amber',
    items: [
      'Tipo de estudio (descriptivo, experimental, cualitativo, etc.)',
      'Metodología y técnicas de recolección de datos',
      'Población y muestra del estudio',
      'Instrumentos utilizados (encuestas, entrevistas, software, etc.)',
      'Análisis de datos previsto',
      'Lugar de ejecución',
      'Cronograma de actividades por fases',
    ],
  },
  {
    icon: 'ri-team-line',
    titulo: 'Equipo de Autores',
    color: 'teal',
    items: [
      'Nombre completo de cada autor',
      'Afiliación principal de cada autor',
      'ORCID de cada autor (opcional pero recomendado)',
      'Presupuesto estimado en Bs. (opcional)',
      'Fuente de financiamiento',
    ],
  },
  {
    icon: 'ri-shield-check-line',
    titulo: 'Consideraciones Éticas',
    color: 'rose',
    items: [
      'Indicar si el proyecto requiere evaluación del Comité de Ética',
      'Justificación ética si aplica (riesgos, participantes vulnerables, datos personales)',
    ],
  },
  {
    icon: 'ri-book-open-line',
    titulo: 'Plan de Publicación',
    color: 'teal',
    items: [
      'Revista o congreso donde se planea publicar',
      'Tipo de acceso (abierto o cerrado) e índice si aplica',
      'Fecha estimada de envío del manuscrito',
      'Área del conocimiento',
      'Fecha estimada de inicio del proyecto',
    ],
  },
];

const colorMap: Record<string, { bg: string; border: string; icon: string; badge: string; dot: string }> = {
  teal: {
    bg: 'bg-teal-50',
    border: 'border-teal-100',
    icon: 'bg-teal-100 text-teal-600',
    badge: 'bg-teal-100 text-teal-700',
    dot: 'bg-teal-400',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    icon: 'bg-amber-100 text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-400',
  },
  rose: {
    bg: 'bg-rose-50',
    border: 'border-rose-100',
    icon: 'bg-rose-100 text-rose-600',
    badge: 'bg-rose-100 text-rose-700',
    dot: 'bg-rose-400',
  },
};

export default function RecepcionOrientacion({ proyectos }: Props) {
  const totalRecibidos = proyectos.length;
  const pendientes = proyectos.filter(p =>
    p.estado === 'Pendiente evaluación' || p.estado === 'Pendiente'
  ).length;
  const enEvaluacion = proyectos.filter(p => p.estado === 'En evaluación').length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-md">
            <i className="ri-file-text-line text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Formulario de Recepción</h2>
        </div>
        <p className="text-slate-500 text-sm ml-11">
          Referencia de los datos y documentos que el investigador debe presentar al sistema.
        </p>
      </div>

      {/* Banner informativo */}
      <div className="mb-6 bg-teal-600 rounded-xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0 mt-0.5">
          <i className="ri-information-line text-white text-xl" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm mb-1">
            Esta sección es solo de orientación para el Comité / UGPP
          </p>
          <p className="text-teal-100 text-xs leading-relaxed">
            El formulario de recepción es completado exclusivamente por el <strong className="text-white">investigador o solicitante</strong> desde su portal.
            Aquí puede consultar qué información y documentos debe enviar el investigador, para verificar que la propuesta esté completa al momento de la recepción.
          </p>
        </div>
      </div>

      {/* Stats rápidas */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-teal-700">{totalRecibidos}</p>
          <p className="text-xs text-slate-500 mt-0.5">Proyectos recibidos</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{pendientes}</p>
          <p className="text-xs text-slate-500 mt-0.5">Pendientes de evaluación</p>
        </div>
        <div className="bg-teal-50 border border-teal-100 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-teal-600">{enEvaluacion}</p>
          <p className="text-xs text-slate-500 mt-0.5">En evaluación activa</p>
        </div>
      </div>

      {/* Título sección requisitos */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-5 bg-teal-600 rounded-full" />
        <h3 className="text-base font-semibold text-slate-800">
          Información requerida al investigador
        </h3>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        El investigador debe completar todos los campos marcados como obligatorios en el formulario de recepción.
        A continuación se detallan las secciones y los datos solicitados:
      </p>

      {/* Secciones del formulario */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {REQUISITOS.map((req, idx) => {
          const c = colorMap[req.color];
          return (
            <div
              key={idx}
              className={`${c.bg} border ${c.border} rounded-xl p-5`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${c.icon}`}>
                  <i className={`${req.icon} text-base`} />
                </div>
                <h4 className="text-sm font-semibold text-slate-800">{req.titulo}</h4>
              </div>
              <ul className="space-y-1.5">
                {req.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot} flex-shrink-0 mt-1.5`} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Documentos adjuntos */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center bg-slate-200 rounded-lg">
            <i className="ri-attachment-2 text-slate-600 text-base" />
          </div>
          <h4 className="text-sm font-semibold text-slate-800">Documentos adjuntos requeridos</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { icon: 'ri-file-pdf-line', label: 'Propuesta de investigación completa', req: true },
            { icon: 'ri-file-word-line', label: 'Carta de presentación firmada', req: true },
            { icon: 'ri-shield-check-line', label: 'Aval del Comité de Ética (si aplica)', req: false },
            { icon: 'ri-money-dollar-circle-line', label: 'Presupuesto detallado', req: false },
          ].map((doc, i) => (
            <div key={i} className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
              <span className="w-7 h-7 flex items-center justify-center bg-slate-100 rounded-md flex-shrink-0">
                <i className={`${doc.icon} text-slate-500 text-sm`} />
              </span>
              <span className="text-xs text-slate-700 flex-1">{doc.label}</span>
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${doc.req ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                {doc.req ? 'Obligatorio' : 'Opcional'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Nota final */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0 mt-0.5">
          <i className="ri-alert-line text-amber-600 text-sm" />
        </div>
        <div>
          <p className="text-xs font-semibold text-amber-800 mb-1">Verificación al momento de la recepción</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            Al recibir una propuesta, el Comité debe verificar que todos los campos obligatorios estén completos y que los documentos adjuntos estén presentes antes de proceder a la asignación de código y evaluación formal.
            Si la propuesta está incompleta, se debe notificar al investigador para que complete la información faltante.
          </p>
        </div>
      </div>
    </div>
  );
}
